import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, EyeOff, X } from 'lucide-react';
import type { AppView } from '../../types';
import { kapitelNachId } from '../../tutorial/register';
import {
    ANKER_VERSUCHE,
    ANKER_WARTEN_MS,
    BLASEN_ABSTAND,
    MASKEN_FUELLREGEL,
    MOBIL_GRENZE,
    TOUR_START,
    maskenPfad,
    platziereBlase,
    polstereRechteck,
    schrittText,
    tourZustand,
    type Groesse,
    type Rechteck,
} from './tourLogic';

/**
 * LERNZENTRUM — die geführte Tour in der echten Oberfläche (Paket B).
 *
 * Vollflächige, halbtransparente Maske mit einem Loch über dem Zielelement,
 * daneben eine Sprechblase mit Kapiteltitel, „Schritt n von m", dem Satz aus
 * dem Register und den Knöpfen Zurück / Weiter / Abbrechen. Ziel per
 * `document.querySelector('[data-tutorial="…"]')` — die Anker setzt Auftrag
 * A2b als reine Attribute in die Komponenten.
 *
 * ─── Was diese Komponente NIE tut ───────────────────────────────────────────
 *
 * Sie klickt nichts an und tippt nichts ein (Plan §1.3 und §9). Es gibt in
 * dieser Datei kein `.click()`, kein `.value =`, kein `dispatchEvent`. Bei
 * `aktion: 'klick'` klickt der NUTZER selbst — deshalb ist das Loch
 * durchlässig, und ein Klick auf das Ziel schaltet die Tour weiter.
 *
 * ─── Warum ein SVG-Pfad und nicht vier Rechtecke ────────────────────────────
 *
 * Beides würde das Loch klickdurchlässig machen. Der Pfad gewinnt, weil ein
 * `<path>` mit `fill-rule="evenodd"` beim Treffertest dieselbe Füllregel
 * benutzt wie beim Zeichnen: die Maske schluckt Klicks überall dort, wo sie
 * sichtbar ist, und nur dort — ohne zweite Geometrie, die mit der ersten
 * auseinanderlaufen kann. Vier Rechtecke bräuchten ausserdem eine eigene
 * Rechnung für die runden Ecken und zeigen an den Fugen bei gebrochenen
 * Gerätepixeln helle Haarlinien. Das SVG selbst steht auf
 * `pointer-events: none`, der Pfad holt sich die Ereignisse zurück.
 */

// ─── Props ───────────────────────────────────────────────────────────────────

export interface TourOverlayProps {
    /** Kapitel aus `TUTORIAL_REGISTER`; `null` = keine Tour, nichts wird gerendert. */
    kapitelId: string | null;
    /** Genau einmal je Tour. `schrittIndex` ist der Schritt, auf dem Schluss war. */
    onEnde: (ergebnis: 'fertig' | 'abgebrochen', schrittIndex: number) => void;
    /**
     * Angeboten, wenn ein Anker nicht auftaucht und das Kapitel in einer anderen
     * Ansicht startet als der, in der wir gerade stehen. Fehlt der Callback,
     * bleibt es beim Hinweis mit Weiter/Abbrechen.
     */
    onNavigiere?: (view: AppView) => void;
}

// ─── Kleinteile ──────────────────────────────────────────────────────────────

/** Polster zwischen Ziel und Lochrand; der Ring sitzt genau auf dieser Kante. */
const LOCH_POLSTER = 6;
const LOCH_RADIUS = 10;
/** Bis die Blase gemessen ist, rechnen wir mit diesen Massen weiter. */
const BLASE_VORGABE: Groesse = { breite: 340, hoehe: 210 };

const leseViewport = (): Groesse => ({
    breite: typeof window === 'undefined' ? 0 : window.innerWidth,
    hoehe: typeof window === 'undefined' ? 0 : window.innerHeight,
});

const useReduzierteBewegung = (): boolean => {
    const frage = '(prefers-reduced-motion: reduce)';
    const [reduziert, setReduziert] = useState(
        () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(frage).matches,
    );
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mq = window.matchMedia(frage);
        const merke = () => setReduziert(mq.matches);
        mq.addEventListener('change', merke);
        return () => mq.removeEventListener('change', merke);
    }, []);
    return reduziert;
};

/** Anführungszeichen und Backslash raus — der Anker steht in einem Attributselektor. */
const sicherer = (anker: string): string => anker.replace(/["\\]/g, '');

/** Tippt der Nutzer gerade irgendwo? Dann gehören die Pfeiltasten ihm, nicht uns. */
const schreibtGerade = (): boolean => {
    const a = document.activeElement as HTMLElement | null;
    if (!a) return false;
    if (a.isContentEditable) return true;
    return a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT';
};

// ─── Komponente ──────────────────────────────────────────────────────────────

export const TourOverlay: React.FC<TourOverlayProps> = ({ kapitelId, onEnde, onNavigiere }) => {
    const kapitel = kapitelId ? kapitelNachId(kapitelId) : undefined;
    const reduziert = useReduzierteBewegung();

    const [stand, dispatch] = useReducer(tourZustand, TOUR_START);
    const [ziel, setZiel] = useState<HTMLElement | null>(null);
    const [zielRect, setZielRect] = useState<Rechteck | null>(null);
    const [viewport, setViewport] = useState<Groesse>(leseViewport);
    const [blaseGroesse, setBlaseGroesse] = useState<Groesse>(BLASE_VORGABE);

    const blaseRef = useRef<HTMLDivElement | null>(null);
    const gemeldetRef = useRef<string | null>(null);

    const laeuft = stand.phase === 'laeuft' && stand.kapitelId === kapitelId && !!kapitel;
    const schritt = kapitel && laeuft ? kapitel.schritte[stand.index] : undefined;
    const anker = schritt?.anker ?? null;

    // ── Start / Wechsel des Kapitels ─────────────────────────────────────────
    useEffect(() => {
        if (!kapitelId) return;
        const k = kapitelNachId(kapitelId);
        if (!k) {
            // Unbekannte ID (altes Lesezeichen, umbenanntes Kapitel): still
            // abräumen statt eine leere Blase zu zeigen.
            onEnde('abgebrochen', 0);
            return;
        }
        gemeldetRef.current = null;
        dispatch({ typ: 'start', kapitelId, anzahl: k.schritte.length });
        // onEnde absichtlich nicht in den Abhängigkeiten: der Callback wechselt
        // bei jedem Render des Containers die Identität und würde die Tour
        // endlos neu starten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kapitelId]);

    // ── Ende melden, genau einmal ────────────────────────────────────────────
    useEffect(() => {
        if (stand.phase !== 'fertig' && stand.phase !== 'abgebrochen') return;
        const marke = `${stand.kapitelId ?? ''}#${stand.phase}`;
        if (gemeldetRef.current === marke) return;
        gemeldetRef.current = marke;
        onEnde(stand.phase, stand.index);
    }, [stand.phase, stand.kapitelId, stand.index, onEnde]);

    // ── Anker suchen: sofort, dann drei Wiederholungen ───────────────────────
    useEffect(() => {
        if (!laeuft || !anker) {
            setZiel(null);
            return;
        }
        setZiel(null);
        let abgebrochen = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let versuch = 0;

        const suche = () => {
            if (abgebrochen) return;
            const gefunden = document.querySelector<HTMLElement>(`[data-tutorial="${sicherer(anker)}"]`);
            if (gefunden) {
                setZiel(gefunden);
                dispatch({ typ: 'ankerGefunden' });
                return;
            }
            versuch += 1;
            dispatch({ typ: 'ankerFehlschlag' });
            // Modale und Reiter brauchen einen Moment, bis ihr Anker im DOM steht.
            if (versuch < ANKER_VERSUCHE) timer = setTimeout(suche, ANKER_WARTEN_MS);
        };

        suche();
        return () => {
            abgebrochen = true;
            if (timer) clearTimeout(timer);
        };
    }, [laeuft, anker, stand.index]);

    // ── Sichtfenster: eigener Listener, auch ohne Ziel ───────────────────────
    // Im Zustand „Anker fehlt" gibt es kein `ziel` und damit keinen Effekt, der
    // nachmisst. Die Blase steht dann mittig, gerechnet aus `viewport` — bliebe
    // der beim Drehen des Geräts stehen, hinge sie ausserhalb des Bildes.
    useEffect(() => {
        const merke = () =>
            setViewport((alt) => {
                const neu = leseViewport();
                return alt.breite === neu.breite && alt.hoehe === neu.hoehe ? alt : neu;
            });
        window.addEventListener('resize', merke);
        return () => window.removeEventListener('resize', merke);
    }, []);

    // ── Ziel ins Bild holen und vermessen; Neupositionierung per rAF ─────────
    useEffect(() => {
        if (!ziel) {
            setZielRect(null);
            return;
        }
        let raf = 0;
        const nachzuegler: ReturnType<typeof setTimeout>[] = [];

        const mess = () => {
            raf = 0;
            const r = ziel.getBoundingClientRect();
            setZielRect({ top: r.top, left: r.left, breite: r.width, hoehe: r.height });
            setViewport(leseViewport());
        };
        const plan = () => {
            if (!raf) raf = window.requestAnimationFrame(mess);
        };

        ziel.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduziert ? 'auto' : 'smooth' });
        mess();
        // Sanftes Scrollen läuft asynchron weiter; Scroll-Ereignisse fangen das
        // meiste ab, diese Nachmessungen den Rest (Ende der Animation).
        for (const ms of [150, 350, 650]) nachzuegler.push(setTimeout(plan, ms));

        window.addEventListener('resize', plan);
        window.addEventListener('scroll', plan, true);
        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            for (const t of nachzuegler) clearTimeout(t);
            window.removeEventListener('resize', plan);
            window.removeEventListener('scroll', plan, true);
        };
    }, [ziel, reduziert]);

    // ── Blasengrösse messen (für die Platzierung) ────────────────────────────
    useLayoutEffect(() => {
        const el = blaseRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const merke = () => {
            const r = el.getBoundingClientRect();
            setBlaseGroesse((alt) =>
                Math.abs(alt.breite - r.width) < 1 && Math.abs(alt.hoehe - r.height) < 1
                    ? alt
                    : { breite: r.width, hoehe: r.height },
            );
        };
        merke();
        const beobachter = new ResizeObserver(merke);
        beobachter.observe(el);
        return () => beobachter.disconnect();
    }, [laeuft, stand.index, stand.ankerFehlt]);

    // ── Der Nutzer klickt das Ziel selbst → Tour geht weiter ─────────────────
    useEffect(() => {
        if (!ziel || schritt?.aktion !== 'klick') return;
        // Capture-Phase und KEIN preventDefault: die App bekommt den Klick
        // unverändert, wir hören nur mit. Der nächste Anker steht danach oft
        // erst in ein paar Hundert Millisekunden im DOM — dafür gibt es die
        // Wiederholungen in der Ankersuche.
        const weiter = () => dispatch({ typ: 'weiter' });
        ziel.addEventListener('click', weiter, true);
        return () => ziel.removeEventListener('click', weiter, true);
    }, [ziel, schritt?.aktion]);

    // ── Tastatur ─────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!laeuft) return;
        const taste = (e: KeyboardEvent) => {
            // Tippt jemand, gehören ALLE diese Tasten ihm: die Pfeile dem
            // Textcursor, Escape dem Feld oder dem Dialog darüber. Sonst
            // räumte ein Escape, das nur ein Modal schliessen soll, nebenbei
            // die Tour mit ab.
            if (schreibtGerade()) return;
            if (e.key === 'Escape') {
                // KEIN preventDefault: der Listener hängt am window, und
                // was sonst noch auf Escape hört (Modal, Menü, Auswahlliste),
                // soll seine Reaktion behalten.
                dispatch({ typ: 'abbrechen' });
                return;
            }
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                dispatch({ typ: 'weiter' });
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                dispatch({ typ: 'zurueck' });
            }
        };
        window.addEventListener('keydown', taste);
        return () => window.removeEventListener('keydown', taste);
    }, [laeuft]);

    // ── Fokus in die Blase ───────────────────────────────────────────────────
    useEffect(() => {
        if (!laeuft) return;
        blaseRef.current?.focus({ preventScroll: true });
    }, [laeuft, stand.index]);

    const abbrechen = useCallback(() => dispatch({ typ: 'abbrechen' }), []);
    const weiter = useCallback(() => dispatch({ typ: 'weiter' }), []);
    const zurueck = useCallback(() => dispatch({ typ: 'zurueck' }), []);

    const mobil = viewport.breite <= MOBIL_GRENZE;
    const loch = useMemo(() => (zielRect ? polstereRechteck(zielRect, LOCH_POLSTER) : null), [zielRect]);
    const pfad = useMemo(
        () => maskenPfad(loch ?? { top: 0, left: 0, breite: 0, hoehe: 0 }, viewport, LOCH_RADIUS),
        [loch, viewport],
    );
    const platz = useMemo(() => {
        if (!loch) {
            // Kein Ziel (Hinweis „nicht sichtbar"): Blase mittig, damit sie
            // nicht in einer Ecke auf ein Element zeigt, das es nicht gibt.
            const breite = mobil ? viewport.breite : blaseGroesse.breite;
            return {
                top: mobil ? Math.max(0, viewport.hoehe - blaseGroesse.hoehe) : Math.max(24, viewport.hoehe / 2 - blaseGroesse.hoehe / 2),
                left: mobil ? 0 : Math.max(24, viewport.breite / 2 - breite / 2),
                seite: 'unten' as const,
            };
        }
        return platziereBlase(loch, blaseGroesse, viewport, BLASEN_ABSTAND);
    }, [loch, blaseGroesse, viewport, mobil]);

    if (!kapitel || !laeuft || !schritt) return null;

    const anzahl = kapitel.schritte.length;
    const letzter = stand.index === anzahl - 1;
    const andereAnsicht = !!onNavigiere && stand.ankerFehlt;

    // Zeiger auf das Ziel: mittig zum Loch, aber nie über die Blasenecke hinaus.
    const zeiger = loch
        ? {
              left: Math.min(Math.max(loch.left + loch.breite / 2 - platz.left, 20), Math.max(20, blaseGroesse.breite - 20)),
              top: Math.min(Math.max(loch.top + loch.hoehe / 2 - platz.top, 20), Math.max(20, blaseGroesse.hoehe - 20)),
          }
        : null;
    const zeigerZeigen = !mobil && !!zeiger && !stand.ankerFehlt;

    return createPortal(
        <div className="fixed inset-0 z-[400]" style={{ pointerEvents: 'none' }} data-tour-overlay="1">
            {/*
              * Die Maske: SVG ohne Zeigerereignisse, der Pfad holt sie sich
              * zurück. Damit schluckt die dunkle Fläche Klicks (sonst bedient
              * der Nutzer versehentlich die App darunter), das Loch nicht.
              */}
            <svg
                className="absolute inset-0"
                width={viewport.breite}
                height={viewport.hoehe}
                viewBox={`0 0 ${viewport.breite} ${viewport.hoehe}`}
                style={{ pointerEvents: 'none' }}
                aria-hidden="true"
            >
                <path
                    d={pfad}
                    fillRule={MASKEN_FUELLREGEL}
                    fill="rgba(15,23,42,.6)"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                />
            </svg>

            {/* Ring um das Loch — reine Deko, nimmt keine Klicks an. */}
            {loch && (
                <div
                    className="fixed rounded-[10px] ring-2 ring-blue-500 ring-offset-0"
                    style={{
                        pointerEvents: 'none',
                        top: loch.top,
                        left: loch.left,
                        width: Math.max(0, loch.breite),
                        height: Math.max(0, loch.hoehe),
                        transition: reduziert ? 'none' : 'top .18s ease, left .18s ease, width .18s ease, height .18s ease',
                    }}
                />
            )}

            <div
                ref={blaseRef}
                role="dialog"
                aria-label={`Tour: ${kapitel.titel}`}
                tabIndex={-1}
                className={`fixed bg-white shadow-2xl border border-slate-200 outline-none ${
                    mobil ? 'rounded-t-2xl' : 'rounded-2xl max-w-[22rem]'
                }`}
                style={{
                    pointerEvents: 'auto',
                    top: platz.top,
                    left: platz.left,
                    width: mobil ? viewport.breite : undefined,
                    transition: reduziert || mobil ? 'none' : 'top .18s ease, left .18s ease',
                }}
            >
                {zeigerZeigen && zeiger && (
                    <div
                        aria-hidden="true"
                        className="absolute w-3 h-3 bg-white border-slate-200 rotate-45"
                        style={
                            platz.seite === 'unten'
                                ? { top: -7, left: zeiger.left - 6, borderLeftWidth: 1, borderTopWidth: 1 }
                                : platz.seite === 'oben'
                                  ? { bottom: -7, left: zeiger.left - 6, borderRightWidth: 1, borderBottomWidth: 1 }
                                  : platz.seite === 'rechts'
                                    ? { left: -7, top: zeiger.top - 6, borderLeftWidth: 1, borderBottomWidth: 1 }
                                    : { right: -7, top: zeiger.top - 6, borderRightWidth: 1, borderTopWidth: 1 }
                        }
                    />
                )}

                <div className="flex items-start justify-between gap-3 px-4 pt-3.5 pb-2">
                    <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-blue-600">Tour</p>
                        <h3 className="font-bold text-slate-800 leading-tight truncate">{kapitel.titel}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={abbrechen}
                        aria-label="Tour abbrechen"
                        className="p-1.5 -mr-1 -mt-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-4 pb-3" aria-live="polite">
                    <p className="text-[11px] font-semibold text-slate-400 mb-1">
                        Schritt {stand.index + 1} von {anzahl}
                    </p>
                    {stand.ankerFehlt ? (
                        <div className="flex gap-2 text-sm text-slate-700">
                            <EyeOff className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                            <div>
                                <p>Dieser Schritt ist gerade nicht sichtbar — öffne die Stelle selbst und klicke Weiter.</p>
                                {schritt.stichwort && <p className="mt-1 text-slate-500">{schritt.stichwort}</p>}
                                <p className="mt-1 text-[11px] text-slate-400 font-mono">{schritt.anker}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-700 leading-snug">{schrittText(schritt)}</p>
                    )}
                    {andereAnsicht && (
                        <button
                            type="button"
                            onClick={() => onNavigiere?.(kapitel.view)}
                            className="mt-2 text-xs font-bold text-blue-600 hover:text-blue-700 underline underline-offset-2"
                        >
                            Zur passenden Ansicht wechseln
                        </button>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-100">
                    <div className="flex items-center gap-1" role="group" aria-label="Fortschritt">
                        {kapitel.schritte.map((s, i) => (
                            <button
                                key={`${s.anker}-${i}`}
                                type="button"
                                onClick={() => dispatch({ typ: 'springe', index: i })}
                                aria-label={`Schritt ${i + 1} von ${anzahl}`}
                                aria-current={i === stand.index ? 'step' : undefined}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === stand.index ? 'w-4 bg-blue-600' : i < stand.index ? 'w-1.5 bg-blue-300' : 'w-1.5 bg-slate-200'
                                }`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        <button
                            type="button"
                            onClick={zurueck}
                            disabled={stand.index === 0}
                            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" /> Zurück
                        </button>
                        <button
                            type="button"
                            onClick={weiter}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            {letzter ? (
                                <>
                                    Fertig <Check className="w-3.5 h-3.5" />
                                </>
                            ) : (
                                <>
                                    Weiter <ArrowRight className="w-3.5 h-3.5" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TourOverlay;
